import * as turf from '@turf/turf'

// พื้นที่ของนโยบาย "เส้นทางรถโม่ห้ามเข้า" (cpac_policy.policy_type = 10)
// ที่สร้างอัตโนมัติตอนบันทึกเส้นทาง small_truck_route = 2 ในหน้า smartdispatching
//
// เส้นทางใน cpac_route เป็นเส้น (polyline) แต่ cpac_policy ตรวจด้วย polygon
// จึงต้องขยายเส้นออกข้างละ 10 เมตร (corridor) ก่อนส่งให้ backend
//
// คำนวณฝั่ง frontend เพราะใช้ @turf/turf — ฝั่ง PHP ไม่มีไลบรารีเทียบเท่า
// backend (SmallTruckRouteController::CreateData) เอาผลลัพธ์ไปสร้าง cpac_policy
// พร้อมเส้นทางใน transaction เดียวกัน แล้วผูกไว้ที่ cpac_route_policy_map

// small_truck_route ของเส้นทางรถโม่ห้ามเข้า
export const MIXER_TRUCK_PROHIBITED_ROUTE_TYPE = '2'

const BUFFER_METERS = 10 // ขยายข้างละ 10 ม. (กว้างรวม 20 ม.)
const BUFFER_STEPS = 8 // จำนวนจุดต่อ 1/4 วงกลมที่หัวท้ายเส้น
const SIMPLIFY_TOLERANCE = 0.00001 // Douglas-Peucker ลดจุดหลัง buffer
const DECIMALS = 6 // cpac_policy.lat/lng = decimal(10,6)

// geoFene ของหน้า smartdispatching = array แบน [lat, lng, lat, lng, ...]
// คืน [[lng, lat], ...] ตามที่ turf ต้องการ และตัดจุดซ้ำติดกันออก (segment ยาว 0 ทำ buffer เพี้ยน)
function toCoords(geoFene) {
  const flat = (Array.isArray(geoFene)
    ? geoFene
    : String(geoFene == null ? '' : geoFene).split(',')
  )
    .map(v => Number(String(v).trim()))
    .filter(v => Number.isFinite(v))

  // จำนวนค่าเป็นเลขคี่ = ค่าสุดท้ายไม่มีคู่ ตัดทิ้ง
  const usable = flat.length % 2 === 0 ? flat : flat.slice(0, flat.length - 1)

  const coords = []
  for (let i = 0; i < usable.length; i += 2) {
    const lat = usable[i]
    const lng = usable[i + 1]
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) continue
    const prev = coords[coords.length - 1]
    if (prev && prev[0] === lng && prev[1] === lat) continue
    coords.push([lng, lat])
  }
  return coords
}

// เลือก polygon ชิ้นที่ใหญ่สุดถ้า buffer ออกมาเป็น MultiPolygon
function largestPolygon(feature) {
  const g = feature.geometry
  if (!g) return null
  if (g.type === 'Polygon') return g.coordinates
  if (g.type === 'MultiPolygon') {
    let best = null
    let bestArea = -1
    for (const rings of g.coordinates) {
      const area = turf.area(turf.polygon(rings))
      if (area > bestArea) {
        bestArea = area
        best = rings
      }
    }
    return best
  }
  return null
}

// ผ่ารู 1 วงเข้ากับ ring นอก (ทั้งสอง ring ปิดท้ายด้วยจุดแรก)
// cpac_policy.geo_fence เก็บเป็น string "lat,lng,..." วงเดียว เก็บรู (interior ring) ไม่ได้
// หาคู่จุด (นอก, ใน) ที่ใกล้กันสุด แล้วแทรกวงในเข้าไปตรงนั้น รอยผ่ากว้าง 0
// ray casting (CpacPolicy::checkInsidePolygon) ตัดขาเข้า-ขาออกพร้อมกัน ความคี่-คู่จึงไม่เปลี่ยน
// ทิศของวงในต้องคงเดิม (turf.buffer คืนวงนอกทวนเข็ม วงในตามเข็มตาม GeoJSON spec แล้ว)
function slitHole(outerClosed, holeClosed) {
  const o = outerClosed.slice(0, -1)
  const h = holeClosed.slice(0, -1)
  if (!o.length || !h.length) return outerClosed

  let bi = 0
  let bj = 0
  let best = Infinity
  for (let i = 0; i < o.length; i++) {
    for (let j = 0; j < h.length; j++) {
      const dx = o[i][0] - h[j][0]
      const dy = o[i][1] - h[j][1]
      const v = dx * dx + dy * dy
      if (v < best) {
        best = v
        bi = i
        bj = j
      }
    }
  }

  const rotated = h.slice(bj).concat(h.slice(0, bj))
  const merged = o.slice(0, bi + 1).concat(rotated, [rotated[0]], o.slice(bi))
  return merged.concat([merged[0]])
}

// เส้นทาง (polyline) -> พื้นที่นโยบาย (polygon)
// คืน { geo_fence, lat, lng, points } ส่งเป็นฟิลด์ policy_area ของ /small-truck-route/create
// geo_fence เป็น array แบน [lat, lng, ...] ตามที่ CpacPolicy::CreatePolicy คาดไว้
// (implode ลง cpac_policy.geo_fence แล้วอ่านคู่ละ 2 ค่า)
// โยน Error พร้อมข้อความไทยถ้าสร้างไม่ได้
export function buildProhibitedRoutePolicyArea(geoFene) {
  const coords = toCoords(geoFene)
  if (coords.length < 2) {
    throw new Error('เส้นทางมีจุดไม่ถึง 2 จุด สร้างพื้นที่นโยบายไม่ได้')
  }

  const line = turf.lineString(coords)

  let buffered
  try {
    buffered = turf.buffer(line, BUFFER_METERS, {
      units: 'meters',
      steps: BUFFER_STEPS,
    })
  } catch (e) {
    throw new Error(`ขยายแนวเส้นทางไม่สำเร็จ: ${e.message}`)
  }
  if (!buffered || !buffered.geometry) {
    throw new Error('ขยายแนวเส้นทางไม่สำเร็จ (ได้ค่าว่าง)')
  }

  let simplified
  try {
    simplified = turf.simplify(buffered, {
      tolerance: SIMPLIFY_TOLERANCE,
      highQuality: true,
    })
  } catch (e) {
    // ลดจุดไม่ได้ก็ใช้ของเดิม จุดเยอะขึ้นแต่พื้นที่ยังถูก
    simplified = buffered
  }

  const rings = largestPolygon(simplified)
  if (!rings || !rings.length) {
    throw new Error('ผลลัพธ์ที่ได้ไม่ใช่พื้นที่ปิด สร้างพื้นที่นโยบายไม่ได้')
  }

  let ringClosed = rings[0]
  for (const hole of rings.slice(1)) ringClosed = slitHole(ringClosed, hole)

  // ตัดจุดปิดท้ายที่ซ้ำจุดแรกออก ให้ตรง convention ของ geo_fence ที่ฟอร์มนโยบายเขียน
  const ring = ringClosed.slice()
  const first = ring[0]
  const last = ring[ring.length - 1]
  if (first[0] === last[0] && first[1] === last[1]) ring.pop()
  if (ring.length < 3) {
    throw new Error(`พื้นที่ที่ได้เหลือ ${ring.length} จุด น้อยเกินไป`)
  }

  // จุดศูนย์กลาง = จุดกลางตามแนวเส้นทาง ไม่ใช่กลาง bbox
  // corridor เป็นริบบิ้นยาว ถ้าเส้นคดเคี้ยว กลาง bbox จะตกนอกพื้นที่
  const midPoint = turf.along(
    line,
    turf.length(line, { units: 'meters' }) / 2,
    { units: 'meters' },
  )

  return {
    geo_fence: ring.reduce((acc, [lng, lat]) => {
      acc.push(lat.toFixed(DECIMALS), lng.toFixed(DECIMALS))
      return acc
    }, []),
    lat: midPoint.geometry.coordinates[1].toFixed(DECIMALS),
    lng: midPoint.geometry.coordinates[0].toFixed(DECIMALS),
    points: ring.length,
  }
}
